<template>
  <div class="custom-service">
    <div v-if="!hasCustomServices" class="custom-service__empty-state">
      <span>{{
        $t("message.facilityDetails.services.missingServiceQuestion")
      }}</span>

      <CustomServiceAddButton
        :label="$t('message.facilityDetails.services.addCustomService')"
        @click="handleAddServiceRow"
      />
    </div>
    <template v-else>
      <vue-perfect-scrollbar
        ref="listWrapper"
        class="custom-service__list-wrapper"
      >
        <v-form
          class="config-wrapper"
          ref="form"
          v-model="isFormValid"
          @submit.prevent
        >
          <GmbServiceCustomServiceListItem
            v-for="service in customServicesList"
            v-model="service.name"
            v-validate="'service_exists|max-length:120'"
            :data-vv-name="service.id"
            :error-messages="errors.collect(service.id)"
            :key="service.id"
            @remove-custom-service="handleRemoveService(service.id)"
            @change="handleChange"
            @add-custom-service="handleAddServiceRow"
          />
        </v-form>
      </vue-perfect-scrollbar>
      <CustomServiceAddButton
        :label="$t('message.facilityDetails.services.addAnotherCustomService')"
        class="service-button"
        @click="handleAddServiceRow"
      />
    </template>
  </div>
</template>

<script>
import cuid from "cuid";
import {
  SERVICE_ACTIONS_ENUM,
  SERVICE_ITEM_TYPE_ENUM
} from "@/enums/gmb-services.enum";
import GmbServiceCustomServiceAddButton from "./GmbServiceCustomServiceAddButton.vue";
import { Validator } from "vee-validate";
export default {
  name: "GmbServiceCustomService",
  components: {
    GmbServiceCustomServiceListItem: () =>
      import("./GmbServiceCustomServiceListItem.vue"),
    CustomServiceAddButton: GmbServiceCustomServiceAddButton
  },
  props: {
    servicesList: {
      type: Array,
      default: () => []
    },
    selectedCategoryId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      customServicesList: [],
      isFormValid: false
    };
  },
  computed: {
    hasCustomServices() {
      return !!this.customServicesList.length;
    }
  },
  mounted() {
    Validator.extend("service_exists", {
      getMessage: (fieldId, fieldValue) => {
        return this.$t("message.facilityDetails.services.duplicateError", [
          fieldValue[0]
        ]);
      },
      validate: value => {
        return {
          valid: !this.checkIfServiceExists(value),
          data: value
        };
      }
    });
  },
  methods: {
    checkIfServiceExists(serviceName) {
      const serviceNameLower = serviceName.toLowerCase();
      return this.servicesList.some(
        service =>
          this.isDisplayNameMatch(service, serviceNameLower) ||
          this.isGmbServiceTypeMatch(service, serviceNameLower)
      );
    },
    isDisplayNameMatch(service, serviceNameLower) {
      return (
        service.display_name &&
        service.display_name.toLowerCase() === serviceNameLower
      );
    },
    isGmbServiceTypeMatch(service, serviceNameLower) {
      return (
        service.gmb_service_type &&
        service.gmb_service_type.display_name.toLowerCase() === serviceNameLower
      );
    },
    handleAddServiceRow() {
      this.customServicesList.push({ id: cuid(), name: "" });
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    handleRemoveService(id) {
      this.customServicesList = this.customServicesList.filter(
        service => service.id !== id
      );
      if (this.customServicesList.length === 0) {
        this.isFormValid = true;
      }
      this.handleChange();
    },
    clearServicesList() {
      this.customServicesList = [];
    },
    createServiceItems(customServices) {
      return customServices.map(service => ({
        action: SERVICE_ACTIONS_ENUM.CREATE,
        item: {
          type: SERVICE_ITEM_TYPE_ENUM.FREE_FORM,
          display_name: service.name,
          gmb_category: this.selectedCategoryId
        }
      }));
    },
    handleChange() {
      const updatedServicesList = this.createServiceItems(
        this.customServicesList
      );

      this.$nextTick(() => {
        this.$validator.validateAll().then(result => {
          if (result) {
            this.$emit("change", updatedServicesList);
          }
        });
      });
    },
    scrollToBottom() {
      const ps = this.$refs.listWrapper.ps;
      const contentHeight = ps.element.scrollHeight;
      ps.element.scrollTop = contentHeight;
      ps.update();
    }
  },
  watch: {
    isFormValid(current) {
      this.$emit("custom-form-validation", current);
    }
  }
};
</script>
<style lang="scss" scoped>
::v-deep {
  .custom-service-btn {
    margin-top: 0 !important;
  }
}
.custom-service {
  &__list-wrapper {
    max-height: 160px;
    padding-bottom: $spacing * 0.2;
  }

  &__empty-state {
    @include font-size(12px);
    display: flex;
    align-items: center;
    gap: $spacing;

    span {
      color: $grey-darker;
    }
  }
}
</style>
